import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["events", "currentTime", "displayYear", "displayDay", "timezone"];
import { Component } from 'react';
import I18n from 'I18n';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import AllDayEventsCount from 'ui-addon-calendars/components/UIDayWeekLayout/AllDayEventsCount';
import { trackCalendarInteraction } from 'ui-addon-calendars/lib/usageTracker';
import UIDayWeekLayout from '../../components/UIDayWeekLayout';
import CALENDAR_VIEW_TYPES from '../../constants/CalendarViewTypes';
import { getDayOfWeek, diff, isSame, getMomentForDay, getMinute } from '../../lib/time';
// @ts-expect-error not yet typed
import { groupOverlappingEvents } from '../../lib/eventParse';
import WeekDay from '../WeekDay';
import { Event, AbsoluteWrapper, DayOfWeekText } from './DayCalendarStyles';
import { eventDefaultLength, eventMinLength as defaultEventMinLength } from '../../constants/sizes';
import DayOfWeek from '../DayOfWeek';
import { getEventDisplayState, toggleAllDayEvents } from '../../lib/eventDisplay';
import { AllDayEvent, AllDayEventsContainer } from '../../components/AllDayEvents';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default class UIDayCalendar extends Component {
  constructor(props) {
    super(props);
    this.state = getEventDisplayState(props.eventDisplay);
  }
  componentDidUpdate(prevProps) {
    if (prevProps.eventDisplay !== this.props.eventDisplay) {
      this.setState(Object.assign({}, getEventDisplayState(this.props.eventDisplay)));
    }
  }
  renderAllDayEvent(event, index) {
    const {
      start,
      end,
      placeholder
    } = event.props;
    const {
      timezone
    } = this.props;
    const startDay = getDayOfWeek(start, timezone);
    const length = diff(end, start, 'days') + 1;
    if (placeholder) return null;
    return /*#__PURE__*/_jsx(AllDayEvent, {
      "data-test-id": "day-calendar-all-day-event",
      index: index,
      length: length,
      children: event
    }, `${startDay}.${length}.${index}`);
  }
  renderEvent(event, hour, overlapGroupIndex, index, numberOfChildren) {
    if (event.props.isPlaceholder) return null;
    const {
      start,
      length
    } = event.props;
    const {
      useExactEventTimes,
      eventMinLength,
      timezone
    } = this.props;
    const calculatedLength = Math.max(length, useExactEventTimes ? eventMinLength : eventDefaultLength);
    // Ensure event doesn't stretch beyond 24-hour day boundary
    const remainingHoursInDay = 24 - hour;
    const adjustedLength = Math.min(calculatedLength, remainingHoursInDay);
    return /*#__PURE__*/_jsx(Event, {
      hour: hour,
      minute: useExactEventTimes ? getMinute(start, timezone) : undefined,
      index: index,
      totalEvents: numberOfChildren,
      length: adjustedLength,
      children: event
    }, `${hour}.${overlapGroupIndex}.${index}`);
  }
  renderDisplayEvents(events, hour) {
    if (!this.props.useExactEventTimes) {
      return events.map((event, index) => this.renderEvent(event, hour, 0, index, events.size));
    }
    const groupedEvents = groupOverlappingEvents(events);
    return groupedEvents.map((group, groupIndex) => /*#__PURE__*/_jsx("span", {
      children: group.map((event, index) => this.renderEvent(event, hour, groupIndex, index, group.size))
    }, groupIndex));
  }
  renderDay(events) {
    const {
      currentTime,
      _incrementCurrentTime,
      displayYear,
      displayDay,
      timezone
    } = this.props;
    const actualDate = getMomentForDay(displayYear, displayDay, timezone);
    const isCurrentDay = isSame(actualDate, currentTime, timezone, 'day');
    return /*#__PURE__*/_jsx(WeekDay, {
      isCurrentDay: isCurrentDay,
      droppedMoment: actualDate,
      currentTime: currentTime,
      _incrementCurrentTime: _incrementCurrentTime,
      children: events ? /*#__PURE__*/_jsx(AbsoluteWrapper, {
        children: events.map((eventsForHour, index) => /*#__PURE__*/_jsx("span", {
          children: this.renderDisplayEvents(eventsForHour, index)
        }, index))
      }) : null
    });
  }
  renderAllDayEvents() {
    const {
      allDayEvents
    } = this.props;
    if (allDayEvents.size === 0) return null;
    const events = allDayEvents.map((event, index) => {
      return this.renderAllDayEvent(event, index);
    });
    if (this.state.showAllDayEvents === 'collapsed') {
      return /*#__PURE__*/_jsx(AllDayEventsCount, {
        onToggleAllDayEvents: () => this.setState(prev => ({
          showAllDayEvents: toggleAllDayEvents(prev.showAllDayEvents)
        }), () => {
          trackCalendarInteraction({
            action: 'expand all-day events day header',
            viewType: CALENDAR_VIEW_TYPES.DAY
          });
        }),
        totalEvents: events.size,
        view: CALENDAR_VIEW_TYPES.DAY
      });
    }
    return /*#__PURE__*/_jsx(AllDayEventsContainer, {
      children: events
    });
  }
  render() {
    const _this$props = this.props,
      {
        events,
        currentTime,
        displayYear,
        displayDay,
        timezone
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const day = getMomentForDay(displayYear, displayDay, timezone);
    const weekDayKey = day.locale('en').format('dd');
    const isCurrentDay = isSame(day, currentTime, timezone, 'day');
    return /*#__PURE__*/_jsx(UIDayWeekLayout, Object.assign({}, rest, {
      displayYear: displayYear,
      displayDay: displayDay,
      header: /*#__PURE__*/_jsx(DayOfWeek, {
        formattedDate: day.locale(I18n.moment.locale()).format('YYYY-MM-DD'),
        weekDayKey: weekDayKey,
        dayOfWeekNumber: 0,
        children: /*#__PURE__*/_jsxs(TFlex, {
          align: "center",
          gap: 6,
          children: [/*#__PURE__*/_jsx("span", {
            children: day.locale(I18n.moment.locale()).format('ddd')
          }), /*#__PURE__*/_jsx(DayOfWeekText, {
            isCurrentDay: isCurrentDay,
            children: day.date()
          })]
        })
      }),
      allDayEventsContent: this.renderAllDayEvents(),
      view: CALENDAR_VIEW_TYPES.DAY,
      showAllDayEvents: this.state.showAllDayEvents,
      showEvents: this.state.showEvents,
      onToggleAllDayEvents: () => this.setState(prev => ({
        showAllDayEvents: toggleAllDayEvents(prev.showAllDayEvents)
      }), () => {
        trackCalendarInteraction({
          action: this.state.showAllDayEvents === 'expanded' ? 'expand all-day events' : 'collapse all-day events',
          viewType: CALENDAR_VIEW_TYPES.DAY
        });
      }),
      children: this.renderDay(events)
    }));
  }
}
UIDayCalendar.defaultProps = {
  displayDay: 1,
  eventMinLength: defaultEventMinLength
};